import { PERMISSIONS, PROPERTY_STATUS } from '@ibe/constants';
import { ErrorState, LoadingState } from '@ibe/ui';
import { useNavigate, useParams } from 'react-router-dom';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { FormPage } from '../../../components/form-page/form-page.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { ApiError } from '../../../lib/api/errors.js';
import { useAppForm } from '../../../lib/forms/use-app-form.js';
import { useCreateProperty, useUpdateProperty } from '../api/mutations.js';
import { useProperty } from '../api/queries.js';
import { type PropertyDetail, type PropertyFormInput, propertyFormInput } from '../api/schemas.js';
import { PropertyFields } from '../forms/property-fields.js';

/**
 * Creating and editing a property.
 *
 * One component for both, because they are the same fields under the same
 * validation; only the defaults, the title and the mutation differ. Splitting
 * them would mean two files that have to be kept in step by hand. Which one
 * this is comes from the route, not a prop, so the URL is the truth.
 *
 * There is no error handling here, and that is the point. `useAppForm` owns
 * schema validation, server field errors attached to their fields, everything
 * else in the feedback dialog, the success message, the double-submit guard
 * and the logging.
 */

/** 409. Named because the linter forbids a bare numeric literal here. */
const HTTP_CONFLICT = 409;
/** `14:00:00` → `14:00`, which is what `<input type="time" step="60">` holds. */
const HOURS_MINUTES = 5;

/**
 * The operator's own time zone, as the starting point.
 *
 * A guess, and a good one: somebody adding a property is usually in it or
 * working its hours. Wrong is one click away, and it beats making everyone
 * choose from four hundred entries to describe the common case.
 */
function localTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

const CREATE_DEFAULTS: PropertyFormInput = {
  name: '',
  code: '',
  displayName: '',
  description: '',
  propertyType: '',
  starRating: '',
  logo: '',
  logoAltText: '',
  timezone: localTimezone(),
  currency: '',
  countryCode: '',
  stateCode: '',
  cityCode: '',
  email: '',
  phone: '',
  phoneCode: '',
  mobile: '',
  website: '',
  abn: '',
  addressLine1: '',
  addressLine2: '',
  addressLine3: '',
  addressLine4: '',
  postCode: '',
  latitude: '',
  longitude: '',
  defaultCheckInTime: '14:00',
  defaultCheckOutTime: '10:00',
  pmsConfigured: false,
  pmsAdapterId: '',
  pmsPropertyId: '',
  // Ignored by the create payload — the service creates every property in
  // DRAFT — and present so one schema can serve both screens.
  status: PROPERTY_STATUS.ACTIVE,
};

/** Null columns become empty strings: a controlled input cannot take null. */
function defaultsFrom(property: PropertyDetail): PropertyFormInput {
  return {
    name: property.name,
    code: property.code,
    displayName: property.displayName ?? '',
    description: property.description ?? '',
    propertyType: property.propertyType ?? '',
    starRating: property.starRating === null ? '' : String(property.starRating),
    logo: property.logo ?? '',
    logoAltText: property.logoAltText ?? '',
    timezone: property.timezone,
    currency: property.currency ?? '',
    countryCode: property.countryCode ?? '',
    stateCode: property.stateCode ?? '',
    cityCode: property.cityCode ?? '',
    email: property.email ?? '',
    phone: property.phone ?? '',
    phoneCode: property.phoneCode ?? '',
    mobile: property.mobile ?? '',
    website: property.website ?? '',
    abn: property.abn ?? '',
    addressLine1: property.addressLine1 ?? '',
    addressLine2: property.addressLine2 ?? '',
    addressLine3: property.addressLine3 ?? '',
    addressLine4: property.addressLine4 ?? '',
    postCode: property.postCode ?? '',
    latitude: property.latitude,
    longitude: property.longitude,
    // The column is `time`, so it arrives with seconds. A time input with a
    // one-minute step will not display them.
    defaultCheckInTime: property.defaultCheckInTime?.slice(0, HOURS_MINUTES) ?? '',
    defaultCheckOutTime: property.defaultCheckOutTime?.slice(0, HOURS_MINUTES) ?? '',
    pmsConfigured: property.pmsConfigured,
    pmsAdapterId: property.pmsAdapterId ?? '',
    pmsPropertyId: property.pmsPropertyId ?? '',
    status: property.status,
  };
}

export function PropertyFormPage(): React.JSX.Element {
  const propertyId = useParams().propertyId;
  const isEdit = propertyId !== undefined;

  return (
    <PermissionGuard
      permission={isEdit ? PERMISSIONS.PROPERTY_UPDATE : PERMISSIONS.PROPERTY_CREATE}
      fallback={<NotPermitted />}
    >
      {isEdit ? <EditProperty propertyId={propertyId} /> : <CreateProperty />}
    </PermissionGuard>
  );
}

function CreateProperty(): React.JSX.Element {
  const navigate = useNavigate();
  const create = useCreateProperty();

  const form = useAppForm<PropertyFormInput>({
    schema: propertyFormInput,
    defaultValues: CREATE_DEFAULTS,
    successMessage: 'Property created',
    onSubmit: async (values) => {
      const created = await create.mutateAsync(values);
      // Straight to the new property: creating one is almost always the first
      // step of configuring it, and returning to the list means finding it
      // again.
      void navigate(`/properties/${created.id}`);
    },
  });

  return (
    <FormPage
      title="New property"
      description="A property is the top-level thing rooms, rates and reservations belong to. It is created in DRAFT and cannot be sold until it is made ACTIVE."
      backTo="/properties"
      backLabel="Properties"
      submitLabel="Create property"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <PropertyFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        setValue={form.setValue}
        mode="create"
      />
    </FormPage>
  );
}

function EditProperty({ propertyId }: { readonly propertyId: string }): React.JSX.Element {
  const navigate = useNavigate();
  const update = useUpdateProperty();
  const query = useProperty(propertyId);

  // The form is mounted only once the record is loaded, so `defaultValues` is
  // populated on first render. Passing them in later needs an effect and a
  // reset, and any field the user has already touched is then discarded.
  if (query.isPending) return <LoadingState label="Loading property" />;
  if (query.error !== null) {
    // An ErrorState rather than NotPermitted: a failed load is not a
    // permission problem, and saying it is sends the reader to the wrong fix.
    return (
      <ErrorState
        description={errorTextFor(query.error) ?? ''}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  return (
    <EditPropertyForm
      propertyId={propertyId}
      property={query.data}
      onSaved={() => {
        void navigate(`/properties/${propertyId}`);
      }}
      save={async (values) => {
        try {
          await update.mutateAsync({ propertyId, version: query.data.version, values });
        } catch (error) {
          // Refetch on conflict only. The message is already on its way to the
          // user from `useAppForm`; what it cannot do is give the form a
          // version that is not stale, so a second attempt would fail
          // identically.
          if (error instanceof ApiError && error.status === HTTP_CONFLICT) {
            await query.refetch();
          }
          throw error;
        }
      }}
    />
  );
}

function EditPropertyForm({
  propertyId,
  property,
  save,
  onSaved,
}: {
  readonly propertyId: string;
  readonly property: PropertyDetail;
  readonly save: (values: PropertyFormInput) => Promise<void>;
  readonly onSaved: () => void;
}): React.JSX.Element {
  const form = useAppForm<PropertyFormInput>({
    schema: propertyFormInput,
    defaultValues: defaultsFrom(property),
    successMessage: 'Property updated',
    onSubmit: async (values) => {
      await save(values);
      onSaved();
    },
  });

  return (
    <FormPage
      title={`Edit ${property.name}`}
      description="Changes take effect immediately for everyone selling this property."
      // The list, not the record being edited. Cancel means "I did not want to
      // make this change", and landing on the detail screen of the record you
      // just abandoned reads as though something was saved.
      backTo="/properties"
      backLabel="Properties"
      submitLabel="Save changes"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <PropertyFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        setValue={form.setValue}
        mode="edit"
        propertyId={propertyId}
      />
    </FormPage>
  );
}
